import React, { useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShieldAlert, Flame } from "lucide-react";
import { confirmAge, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/** Age-gate modal shown once per user before enabling Steamy Mode.
 *  Props:
 *    open: boolean — control visibility
 *    onOpenChange(bool) — called by Radix on open/close
 *    onConfirmed() — called after the backend records age confirmation
 */
export const AgeGate = ({ open, onOpenChange, onConfirmed }) => {
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();

  const confirm = async () => {
    setBusy(true);
    try {
      await confirmAge();
      await refresh();
      onOpenChange?.(false);
      onConfirmed?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="age-gate" className="bg-[#0F1117] border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif text-2xl flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-400" /> Confirm you are 18 or older
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-slate-400 space-y-2">
            <span className="block">
              Steamy Mode unlocks sensual, adult-oriented replies. By continuing, you confirm you
              are at least <span className="text-amber-300 font-semibold">18 years old</span> and
              that you understand ProSeCraft strictly prohibits any content involving minors,
              non-consent, incest, or other illegal material.
            </span>
            <span className="block text-xs text-slate-500 font-mono uppercase tracking-widest flex items-center gap-1">
              <Flame className="h-3 w-3 text-rose-400" /> Violations lock Steamy on your account
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="age-gate-cancel" className="rounded-full">
            Not yet
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="age-gate-confirm"
            onClick={confirm}
            disabled={busy}
            className="rounded-full bg-amber-500 hover:bg-amber-400 text-black"
          >
            I am 18+
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default AgeGate;
